import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Timeout wrapper for fetch requests
const fetchWithTimeout = async (url, options = {}, timeout = 15000) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  
  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    return response;
  } finally {
    clearTimeout(timeoutId);
  }
};

// Retry logic with exponential backoff
const fetchWithRetry = async (url, options = {}, retries = 2, delay = 1000) => {
  for (let i = 0; i <= retries; i++) {
    try {
      const response = await fetchWithTimeout(url, options);
      return response;
    } catch (error) {
      if (i === retries) {
        throw error;
      }
      // Wait before retrying (exponential backoff)
      await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, i)));
    }
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  const refreshUser = useCallback(async (userId, isInitialLoad = false) => {
    setAuthError(null);
    
    try {
      const response = await fetchWithRetry(`${BACKEND_URL}/api/auth/user/${userId}`);
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        localStorage.setItem('gw_user', JSON.stringify(userData));
        return userData;
      } else if (response.status === 404) {
        // User not found, clear storage
        localStorage.removeItem('gw_user');
        setUser(null);
        return null;
      } else {
        // Other HTTP errors
        throw new Error(`Server error: ${response.status}`);
      }
    } catch (error) {
      console.error('Error refreshing user:', error);
      
      // Check if it's a network error
      if (error.name === 'AbortError') {
        setAuthError('Connection timed out. Please check your internet and try again.');
      } else if (error.message.includes('fetch') || error.message.includes('network')) {
        setAuthError('Network error. Please check your internet connection.');
      } else {
        setAuthError('Unable to connect to server. Please try again later.');
      }
      
      // On initial load with network error, use cached data but show warning
      if (isInitialLoad) {
        const savedUser = localStorage.getItem('gw_user');
        if (savedUser) {
          try {
            const cachedUser = JSON.parse(savedUser);
            setUser(cachedUser);
            console.warn('Using cached user data due to network error');
          } catch (e) {
            localStorage.removeItem('gw_user');
            setUser(null);
          }
        }
      }
      
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Check for saved user in localStorage
    const savedUser = localStorage.getItem('gw_user');
    if (savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        // Set cached user immediately for better UX
        setUser(userData);
        // Then refresh from server
        refreshUser(userData.id, true);
      } catch (e) {
        localStorage.removeItem('gw_user');
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, [refreshUser]);

  const login = async (email, password) => {
    setAuthError(null);
    
    try {
      const response = await fetchWithTimeout(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        let errorMessage = 'Login failed';
        try {
          const error = await response.json();
          errorMessage = error.detail || errorMessage;
        } catch (e) {
          // Response wasn't JSON
        }
        throw new Error(errorMessage);
      }

      const userData = await response.json();
      setUser(userData);
      localStorage.setItem('gw_user', JSON.stringify(userData));
      return userData;
    } catch (error) {
      // Handle network errors
      if (error.name === 'AbortError') {
        throw new Error('Connection timed out. Please check your internet and try again.');
      } else if (error.message && (error.message.includes('fetch') || error.message.includes('postMessage') || error.message.includes('cloned'))) {
        throw new Error('Unable to connect to the server. Please try refreshing the page.');
      } else if (error.name === 'TypeError') {
        throw new Error('Network error. Please check your internet connection and try again.');
      }
      throw error;
    }
  };

  const register = async (email, password, name) => {
    setAuthError(null);
    
    try {
      const response = await fetchWithTimeout(`${BACKEND_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, name }),
      });

      if (!response.ok) {
        let errorMessage = 'Registration failed';
        try {
          const error = await response.json();
          errorMessage = error.detail || errorMessage;
        } catch (e) {
          // Response wasn't JSON
        }
        throw new Error(errorMessage);
      }

      const userData = await response.json();
      setUser(userData);
      localStorage.setItem('gw_user', JSON.stringify(userData));
      return userData;
    } catch (error) {
      // Handle network errors
      if (error.name === 'AbortError') {
        throw new Error('Connection timed out. Please check your internet and try again.');
      } else if (error.message && (error.message.includes('fetch') || error.message.includes('postMessage') || error.message.includes('cloned'))) {
        throw new Error('Unable to connect to the server. Please try refreshing the page.');
      } else if (error.name === 'TypeError') {
        throw new Error('Network error. Please check your internet connection and try again.');
      }
      throw error;
    }
  };

  const logout = () => {
    setUser(null);
    setAuthError(null);
    localStorage.removeItem('gw_user');
  };

  const hasAccess = (programId) => {
    if (!user) return false;
    return user.unlocked_programs?.includes(programId) || false;
  };

  const clearAuthError = () => {
    setAuthError(null);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      authError,
      login, 
      register, 
      logout, 
      hasAccess, 
      refreshUser,
      clearAuthError 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
